import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, ArrowUpRight, Check } from "lucide-react";
import api from "@/lib/api";

const PLAN_LABEL = { starter: "Starter", growth: "Growth", enterprise: "Enterprise" };
const PLAN_PRICE = { starter: 29.99, growth: 79.99, enterprise: 199.99 };

/**
 * Shown in place of a module/feature the user's plan doesn't include. Explains the feature,
 * names the required plan and price, and offers an Upgrade CTA — never a broken page.
 */
export default function LockedFeature({ title, description, requiredPlan = "growth", featureKey }) {
  const navigate = useNavigate();
  const [needed, setNeeded] = useState(requiredPlan);

  useEffect(() => {
    if (!featureKey) return;
    api.get("/subscription/plans").then(({ data }) => {
      const p = data.plans.find((pl) => (pl.features || []).includes(featureKey) || (pl.modules || []).includes(featureKey));
      if (p) setNeeded(p.id);
    }).catch(() => {});
  }, [featureKey]);

  const price = PLAN_PRICE[needed];
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4" data-testid="locked-feature">
      <div className="max-w-md w-full rounded-2xl border border-[#D4AF37]/25 bg-[#121214] p-8 text-center">
        <div className="w-14 h-14 rounded-2xl fv-gold-gradient mx-auto flex items-center justify-center mb-5">
          <Lock className="w-6 h-6 text-[#0A0A0B]" />
        </div>
        <h2 className="text-xl font-heading text-white mb-2" data-testid="locked-feature-title">{title}</h2>
        <p className="text-sm text-[#A1A1AA] mb-5">{description}</p>
        <div className="rounded-xl border border-white/10 bg-[#0d0d0f] px-4 py-3 mb-5 flex items-center justify-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-[#D4D4D8]">Included in </span>
          <span className="text-[#D4AF37] font-semibold">{PLAN_LABEL[needed]}</span>
          {price != null && <span className="text-[#71717A]">· ${price}/mo</span>}
        </div>
        <button onClick={() => navigate(`/billing?upgrade=${needed}`)} data-testid="locked-feature-upgrade-btn"
          className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-11 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
          Upgrade to {PLAN_LABEL[needed]} <ArrowUpRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-[#52525B] mt-4 flex items-center justify-center gap-1.5">
          <Check className="w-3.5 h-3.5 text-[#D4AF37]" /> Your existing work is always preserved
        </p>
      </div>
    </div>
  );
}
